if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        // Keep the fixed bar outside theme sections so transformed/animated
        // ancestors cannot affect its viewport positioning on mobile Safari.
        if (this.parentElement !== document.body) {
          document.body.appendChild(this);
          return;
        }

        this.sectionId = this.dataset.sectionId;
        this.select = this.querySelector('[data-sticky-select]');
        this.selectWrap = this.querySelector('.sticky-add-to-cart__select-wrap');
        this.price = this.querySelector('[data-sticky-price]');
        this.selection = this.querySelector('[data-sticky-selection]');
        this.submit = this.querySelector('[data-sticky-submit]');
        this.submitText = this.querySelector('[data-sticky-button-text]');
        this.variantOptions = Array.from(this.select.options).map((option) => ({
          value: option.value,
          title: option.textContent.trim(),
          price: option.dataset.price,
          available: option.dataset.available === 'true',
          options: JSON.parse(option.dataset.options || '[]'),
        }));

        this.select.addEventListener('change', () => this.onSelectionChange());
        this.submit.addEventListener('click', () => this.onSubmit());
        this.refresh();

        this.productObserver = new MutationObserver(() => this.queueRefresh());
        if (this.productInfo) this.productObserver.observe(this.productInfo, { childList: true, subtree: true, attributes: true });

        this.footerTarget = document.querySelector('.footer__content-bottom');
        if (this.footerTarget) {
          this.footerObserver = new IntersectionObserver(([entry]) => {
            this.footerVisible = entry.isIntersecting;
            this.updateVisibility();
          });
          this.footerObserver.observe(this.footerTarget);
        }

        window.addEventListener('scroll', () => this.updateVisibility(), { passive: true });
        window.addEventListener('resize', () => this.updateVisibility(), { passive: true });
      }

      disconnectedCallback() {
        this.productObserver?.disconnect();
        this.buttonObserver?.disconnect();
        this.footerObserver?.disconnect();
      }

      get productInfo() { return document.querySelector(`product-info[data-section="${this.sectionId}"]`); }
      get nativeButton() { return document.getElementById(`ProductSubmitButton-${this.sectionId}`); }

      queueRefresh() {
        cancelAnimationFrame(this.refreshFrame);
        this.refreshFrame = requestAnimationFrame(() => this.refresh());
      }

      refresh() {
        const button = this.nativeButton;
        if (!button) return;
        this.bindVisibilityObserver(button);
        this.syncChoices();
        this.syncButtonState();
        this.updateVisibility();
      }

      bindVisibilityObserver(button) {
        if (this.observedButton === button) return;
        this.buttonObserver?.disconnect();
        this.observedButton = button;
        this.buttonObserver = new IntersectionObserver(() => this.updateVisibility(), { threshold: 0.01 });
        this.buttonObserver.observe(button);
      }

      updateVisibility() {
        const button = this.nativeButton;
        if (!button) return;
        const hasScrolledPast = button.getBoundingClientRect().bottom < 0;
        this.toggleAttribute('hidden', !hasScrolledPast || this.footerVisible);
      }

      get bundleBars() {
        return Array.from(this.productInfo?.querySelectorAll('.kaching-bundles__bar') || []);
      }

      syncChoices() {
        const bars = this.bundleBars;
        if (bars.length) {
          const signature = bars.map((bar) => `${bar.dataset.dealBarId}:${bar.innerText}`).join('|');
          if (this.choiceSignature !== signature || this.choiceKind !== 'bundle') {
            this.choiceKind = 'bundle';
            this.choiceSignature = signature;
            this.select.replaceChildren(...bars.map((bar, index) => {
              const option = document.createElement('option');
              option.value = String(index);
              option.dataset.kind = 'bundle';
              option.textContent = bar.querySelector('.kaching-bundles__bar-title')?.textContent.trim() || `Option ${index + 1}`;
              return option;
            }));
          }
          const selectedIndex = Math.max(0, bars.findIndex((bar) => bar.classList.contains('kaching-bundles__bar--selected')));
          this.select.value = String(selectedIndex);
          const selected = bars[selectedIndex];
          const title = selected?.querySelector('.kaching-bundles__bar-title')?.textContent.trim();
          const price = this.withCurrencyCode(
            selected?.querySelector('.kaching-bundles__bar-price')?.textContent.trim()
          );
          this.updateLabels(title, price, true);
          this.selectWrap.hidden = bars.length < 2;
          return;
        }

        if (this.choiceKind !== 'variant') {
          this.choiceKind = 'variant';
          this.choiceSignature = '';
          this.select.replaceChildren(...this.variantOptions.map((variant) => {
            const option = document.createElement('option');
            option.value = variant.value;
            option.dataset.kind = 'variant';
            option.textContent = variant.title;
            option.disabled = !variant.available;
            return option;
          }));
        }
        const currentId = this.productInfo?.querySelector('form[data-type="add-to-cart-form"] [name="id"]')?.value;
        const variant = this.variantOptions.find((item) => item.value === currentId) || this.variantOptions[0];
        if (!variant) return;
        this.select.value = variant.value;
        this.selectWrap.hidden = this.variantOptions.length < 2;
        this.updateLabels(variant.title, variant.price, variant.available);
      }

      updateLabels(title, price, available) {
        if (title) {
          this.selection.textContent = title;
          if (this.choiceKind === 'bundle' || title !== 'Default Title') this.selection.hidden = false;
        }
        if (price) this.price.textContent = price;
        this.submit.disabled = !available;
        this.submitText.textContent = available
          ? `${this.dataset.addLabel} — ${price || ''}`.trim()
          : this.dataset.soldOutLabel;
      }

      withCurrencyCode(price) {
        const currency = window.Shopify?.currency?.active;
        if (!price || !currency || price.toUpperCase().includes(currency.toUpperCase())) return price;
        return `${price} ${currency}`;
      }

      onSelectionChange() {
        if (this.choiceKind === 'bundle') {
          const bar = this.bundleBars[Number(this.select.value)];
          bar?.querySelector('.kaching-bundles__bar-main')?.click();
          this.queueRefresh();
          return;
        }

        const variant = this.variantOptions.find((item) => item.value === this.select.value);
        const picker = this.productInfo?.querySelector('variant-selects');
        if (!variant || !picker) return;
        const controls = Array.from(picker.querySelectorAll('select, fieldset'));
        let changedControl;
        variant.options.forEach((value, index) => {
          const group = controls[index];
          if (!group) return;
          if (group.tagName === 'SELECT') {
            group.value = value;
            changedControl = group;
          } else {
            const input = Array.from(group.querySelectorAll('input[type="radio"]')).find((item) => item.value === value);
            if (input) { input.checked = true; changedControl = input; }
          }
        });
        changedControl?.dispatchEvent(new Event('change', { bubbles: true }));
        this.updateLabels(variant.title, variant.price, variant.available);
      }

      syncButtonState() {
        const nativeButton = this.nativeButton;
        if (!nativeButton) return;
        const disabled = nativeButton.disabled || nativeButton.getAttribute('aria-disabled') === 'true';
        if (disabled) this.submit.disabled = true;
        const loading = nativeButton.classList.contains('loading') || nativeButton.getAttribute('aria-disabled') === 'true';
        this.submit.classList.toggle('is-loading', loading);
      }

      onSubmit() {
        const nativeButton = this.nativeButton;
        if (!nativeButton || nativeButton.disabled || nativeButton.getAttribute('aria-disabled') === 'true') return;
        this.submit.classList.add('is-loading');
        this.submit.disabled = true;
        nativeButton.click();
        window.setTimeout(() => {
          this.submit.classList.remove('is-loading');
          this.syncChoices();
          this.syncButtonState();
        }, 1200);
      }
    }
  );
}
